/**
 *
 * App
 *
 */
import React, { useEffect, useContext } from 'react';
import { hot } from 'react-hot-loader';
import { Route, Switch, matchPath } from 'react-router-dom';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { makeSelectAppState } from './selectors';
import reducer from './reducer';
import saga from './saga';
import routeTemplates from 'common/route-templates';
import Chat from 'ui/containers/Chat/Loadable';
import AuthenticatedRoute from 'ui/containers/AuthenticatedRoute';
import Notifier from 'ui/containers/Notifier';
import RegisterPage from 'ui/containers/RegisterPage/Loadable';
import LoginPage from 'ui/containers/LoginPage/Loadable';
import ForgotPassword from 'ui/containers/ForgotPassword/Loadable';
import EmailVerify from 'ui/containers/EmailVerify/Loadable';
import OauthRedirect from 'ui/containers/OauthRedirect/Loadable';
import Invite from 'ui/containers/Invite/Loadable';
import ProjectDetailsPage from 'ui/containers/ProjectDetailsPage/Loadable';
import SlackVerify from '../SlackVerify';
import HubspotVerify from '../HubspotVerify';
import MsTeamsVerify from '../MsTeamsVerify';
import SalesforceVerify from '../SalesforceVerify';
import ZendeskVerify from '../ZendeskVerify';
import Iframe from '../Iframe';
import TeamsSuccessPage from './TeamsSuccessPage';
import localStorageTemplates from 'common/local-storage-templates';
import Notification from 'ui/containers/Notification';
import queryString from 'query-string';
import { AuthContext } from 'ui/containers/Providers/AuthProvider';
import { getTeamsOnlyToken } from './actions';

export const App = props => {
  const { history, getTeamsOnlyTokenStart } = props;
  useInjectReducer({ key: 'global', reducer });
  useInjectSaga({ key: 'global', saga });

  const { authenticated, setAuthenticated } = useContext(AuthContext);
  const searchParams = queryString.parse(window.location.search);
  const redirectUrl = searchParams.redirectUrl;
  const isChatOnly = searchParams['chat_only'] && searchParams['chat_only'] === 'true';

  useEffect(() => {
    localStorage.removeItem(localStorageTemplates.insentNetworkStatus);
  }, []);

  useEffect(() => {
    let isInbox = matchPath(props.location.pathname, {
      path: routeTemplates.project.inbox.root,
    });
    if (!isInbox || !isInbox.isExact) {
      isInbox = matchPath(props.location.pathname, {
        path: routeTemplates.project.inbox.detail,
      });
    }
    if (isChatOnly && !authenticated) {
      const {
        tenant: tenantId,
        group: teamId,
        channelId,
        userObjectId,
        userPrincipalName,
      } = searchParams;
      if (tenantId && teamId && channelId && userObjectId && userPrincipalName) {
        getTeamsOnlyTokenStart({
          metadata: {
            payload: {
              tenantId,
              teamId,
              channelId,
              userObjectId,
              userPrincipalName,
            },
            setAuthenticated,
          },
        });
      }
    } else if (redirectUrl && authenticated) {
      history.push(decodeURIComponent(redirectUrl));
    } else if (props.location.pathname === '/' && authenticated) {
      history.push(routeTemplates.project.inbox.detail);
    } else if (props.location.pathname === routeTemplates.iframe) {
      history.push(props.location.pathname + props.location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.location.pathname]);

  return (
    <>
      <Notification />
      <Notifier />
      <Switch>
        <Route exact path={routeTemplates.register} component={RegisterPage} />
        <Route exact path={routeTemplates.iframe} component={Iframe} />
        <Route exact path={routeTemplates.teamsSuccess} component={TeamsSuccessPage} />
        <Route exact path={routeTemplates.login} component={LoginPage} />
        <Route exact path={routeTemplates.passwordReset} component={ForgotPassword} />
        <Route exact path={routeTemplates.emailVerify} component={EmailVerify} />
        <Route exact path={routeTemplates.invite} component={Invite} />
        <Route exact path={routeTemplates.integration.verify.slack} component={SlackVerify} />
        <Route exact path={routeTemplates.integration.verify.hubspot} component={HubspotVerify} />
        <Route exact path={routeTemplates.integration.verify.msteams} component={MsTeamsVerify} />
        <Route exact path={routeTemplates.integration.verify.zendesk} component={ZendeskVerify} />
        <Route exact path={routeTemplates.integration.redirect} component={OauthRedirect} />
        <Route
          exact
          path={routeTemplates.integration.verify.salesforce}
          component={SalesforceVerify}
        />
        {!isChatOnly && (
          <AuthenticatedRoute path={routeTemplates.home} component={ProjectDetailsPage} />
        )}
        <AuthenticatedRoute exact path={routeTemplates.project.inbox.root} component={Chat} />
        <AuthenticatedRoute exact path={routeTemplates.project.inbox.detail} component={Chat} />
      </Switch>
    </>
  );
};

App.propTypes = {};

export const mapStateToProps = (state, props) => {
  return createStructuredSelector({
    app: makeSelectAppState(),
  });
};

export const mapDispatchToProps = dispatch => {
  return {
    getTeamsOnlyTokenStart: ({ metadata }) => dispatch(getTeamsOnlyToken.start({ metadata })),
  };
};

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default hot(module)(compose(withRouter, withConnect)(App));
